import React, { useState, useEffect } from 'react';
import { CloseCircleFilled } from '@ant-design/icons';
import { List, Rate, Row, Col, Tooltip, Button } from 'antd';
import PropTypes from 'prop-types';
import { titlize } from '../../utils/strings';
import { SkillTypeTag } from '../widgets';

export function SkillItem({
	skill,
	index,
	onRemoveBtnClick,
	removeBtnComponent,
	showPlus = true,
	showProficiencyLevels = true,
	plusComponent,
	proficiencyLevels,
	removable
}: any) {
	return (
		<List.Item
			className="align-items-stretch"
			actions={[
				removable &&
					(removeBtnComponent || (
						<Tooltip title="Remove this item." placement="top">
							<a
								onClick={e => {
									e.preventDefault();
									onRemoveBtnClick(index);
								}}
							>
								<CloseCircleFilled style={{ color: '#FF141E', fontSize: '19px' }} />
							</a>
						</Tooltip>
					))
			]}
		>
			<List.Item.Meta
				title={
					<Row gutter={0}>
						<Col xs={{ span: 4 }} xl={{ span: 8 }} className="text-align-left mb-2 mb-xl-1">
							<span
								style={{
									fontSize: '13px',
									color: '#3F536E',
									textAlign: 'end',
									lineHeight: '19px'
								}}
							>
								{titlize(skill.name)}
							</span>
						</Col>
						{showProficiencyLevels && (
							<Col xs={{ span: 16 }} xl={{ span: 11 }} className="text-center mb-2 mb-xl-1">
								<Rate
									disabled
									tooltips={proficiencyLevels}
									value={proficiencyLevels.indexOf(skill.level) + 1}
									style={{ fontSize: '25px' }}
								/>
							</Col>
						)}
						{showPlus
							? plusComponent || (
									<Col xs={{ span: 4 }} xl={{ span: 5 }} className="text-right mb-2 mb-xl-1">
										{skill.isPlus ? (
											<SkillTypeTag type="A Plus">A Plus</SkillTypeTag>
										) : (
											<SkillTypeTag>Required</SkillTypeTag>
										)}
									</Col>
							  )
							: null}
					</Row>
				}
				description={
					<Row className=" mb-2 mb-xl-1" style={{ wordBreak: 'break-word' }}>
						{skill.notes}
					</Row>
				}
			/>
		</List.Item>
	);
}

export default function SkillsList({
	skills,
	proficiencyLevels,
	loadMore,
	emptyText,
	renderItem,
	onItemRemove,
	itemsRemovable = false,
	showPlus = true,
	showProficiencyLevels = true,
	visibleCount = -1
}: any) {
	let [visibleSkills, setSkills] = useState(skills.slice(0, visibleCount > -1 ? visibleCount : skills.length));
	useEffect(() => {
		setSkills(skills.slice(0, visibleCount > -1 ? visibleCount : skills.length));
	}, [skills]);

	const onLoadMore = () => {
		setSkills(skills);
	};

	const onHide = () => {
		setSkills(skills.slice(0, visibleCount > -1 ? visibleCount : skills.length));
	};

	const defaultLoadMore =
		visibleCount > -1 ? (
			visibleSkills.length < skills.length ? (
				<div
					style={{
						textAlign: 'center',
						marginTop: 12,
						height: 32,
						lineHeight: '32px'
					}}
				>
					<Button onClick={onLoadMore}>See {skills.length - visibleSkills.length} more</Button>
				</div>
			) : (
				<div
					style={{
						textAlign: 'center',
						marginTop: 12,
						height: 32,
						lineHeight: '32px'
					}}
				>
					<Button onClick={onHide}>Hide</Button>
				</div>
			)
		) : null;

	const defaultListItem = (skill, index) => (
		<SkillItem
			index={index}
			skill={skill}
			removable={itemsRemovable}
			onRemoveBtnClick={itemsRemovable && onItemRemove}
			proficiencyLevels={proficiencyLevels}
			showPlus={showPlus}
			showProficiencyLevels={showProficiencyLevels}
		/>
	);

	return (
		<List
			itemLayout="horizontal"
			loadMore={loadMore || defaultLoadMore}
			dataSource={visibleSkills}
			locale={{ emptyText: emptyText }}
			renderItem={renderItem || defaultListItem}
		/>
	);
}
